import { v4 as uuidv4 } from "uuid";
import Serializer, {Serialization} from "./Serializer";
import UserInterface from "./UserInterface";
import UserError from "../Util/UserError";
import {reactive} from "vue";
import {Reactive} from "@vue/reactivity";
import ActionHistory from "../ActionHistory/ActionHistory.js";

export type AutoBackup = {
    session_id: string,
    name: string|null,
    date: number,
    state: Serialization,
};

export type AutoBackupReactive = {
    session_id: string,
    name: string|null,
    date: number,
    num_cutouts: number,
    num_routes: number,
    num_locations: number,
    num_shapes: number,
};
export type AutoBackupReactiveData = {
    autobackups: AutoBackupReactive[],
};

export default class AutoBackups {

    static readonly LOCALSTORAGE_KEY = 'auto-backups';

    private static readonly MAX_RETENTION_SECONDS = 7 * 24 * 60 * 60;
    private static readonly MAX_RETENTION_WORKSPACES = 10;
    private static readonly PERIODIC_CHECK_INTERVAL_SECONDS = 60;

    private currentSessionId: string|null = null;
    private currentSessionName: string|null = null;

    private readonly reactiveData: Reactive<AutoBackupReactiveData> = null;
    private hasChanged = false;

    constructor(private readonly userInterface: UserInterface, actionHistory: ActionHistory) {
        this.reactiveData = reactive<AutoBackupReactiveData>({
            autobackups: [],
        });

        this.setNewCurrentSession();
        this.updateReactive();

        actionHistory.onAction(() => {
            this.hasChanged = true;
        });
        actionHistory.onStep(() => {
            this.hasChanged = true;
        });

        this.periodicCheck();
    }

    public setNewCurrentSession(name: string|null = null): void {
        this.currentSessionId = uuidv4();
        this.currentSessionName = name;
        this.hasChanged = false;
    }

    public setCurrentSessionName(name: string|null): void {
        this.currentSessionName = name;
        this.hasChanged = true; // Save the new name in next periodicCheck()
    }

    private updateReactive() {
        this.reactiveData.autobackups = this.getAutoBackups().map((autoBackup: AutoBackup) => <AutoBackupReactive>{
            session_id: autoBackup.session_id,
            name: autoBackup.name,
            date: autoBackup.date,
            num_cutouts: autoBackup.state.cutouts?.length ?? 0,
            num_routes: autoBackup.state.routes?.length ?? 0,
            num_locations: autoBackup.state.locations?.length ?? 0,
            num_shapes: autoBackup.state.shapes?.length ?? 0,
        });
    }

    public resetChanged() {
        this.hasChanged = false;
    }

    private periodicCheck() {
        try {
            if (this.hasChanged) {
                this.storeAutoBackup();
                this.hasChanged = false;
            }

            let deleted = false;
            let i = 0;
            for (const autoBackup of this.reactiveData.autobackups) {
                if ((+(new Date())) - autoBackup.date > AutoBackups.MAX_RETENTION_SECONDS * 1000 || i >= AutoBackups.MAX_RETENTION_WORKSPACES) {
                    this.deleteAutoBackup(autoBackup.session_id);
                    deleted = true;
                }
                i++;
            }
            if (deleted) {
                this.updateReactive();
            }
        } finally {
            setTimeout(() => {
                this.periodicCheck();
            }, AutoBackups.PERIODIC_CHECK_INTERVAL_SECONDS * 1000);
        }
    }

    getReactive(): AutoBackupReactiveData {
        return this.reactiveData;
    }

    getUserInterface(): UserInterface {
        return this.userInterface;
    }

    private fetchAutoBackups(): Record<string, AutoBackup> {
        const item = window.localStorage.getItem(AutoBackups.LOCALSTORAGE_KEY);
        if (item === null) {
            return {};
        }

        return JSON.parse(item);
    }

    private getAutoBackups(): AutoBackup[] {
        const autoBackups = Object.values(this.fetchAutoBackups());
        autoBackups.sort((a, b) => -(a.date - b.date)); // Sort by date descending
        return autoBackups;
    }

    private getAutoBackup(session_id: string): AutoBackup {
        const autoBackups = this.fetchAutoBackups();
        if (autoBackups[session_id] === undefined) {
            throw new UserError('Backup does not exist');
        }
        return autoBackups[session_id];
    }

    loadAutoBackup(session_id: string): Promise<void> {
        const autoBackup = this.getAutoBackup(session_id);
        this.setNewCurrentSession(autoBackup.name);
        return (new Serializer()).unserialize(autoBackup.state, this.userInterface);
    }

    storeAutoBackup(): void {
        const sessionId = this.currentSessionId;
        let name = this.currentSessionName?.trim();

        if (!name) {
            name = null;
        }

        const autoBackup: AutoBackup = {
            session_id: sessionId,
            name: name,
            date: +(new Date()),
            state: (new Serializer()).serializeWorkspace(this.userInterface),
        };

        const autoBackups = this.fetchAutoBackups();
        autoBackups[sessionId] = autoBackup;

        window.localStorage.setItem(AutoBackups.LOCALSTORAGE_KEY, JSON.stringify(autoBackups));

        this.updateReactive();
    }

    deleteAutoBackup(sessionId: string): void {
        const autoBackups = this.fetchAutoBackups();
        if (autoBackups[sessionId] === undefined) {
            throw new UserError('Backup does not exist');
        }
        delete autoBackups[sessionId];
        window.localStorage.setItem(AutoBackups.LOCALSTORAGE_KEY, JSON.stringify(autoBackups));

        this.updateReactive();
    }
}
